/**
 * SectorBreakdown - Horizontal bar visualization of portfolio sector allocation.
 *
 * Data sourced from GET /api/v1/portfolio/sectors.
 * Sorted by percentage descending with proportional bars.
 */

import { cn } from '@/lib/utils';
import type { SectorAllocation } from '@/hooks/usePortfolioSectors';

interface SectorBreakdownProps {
  sectors: SectorAllocation[];
  isLoading: boolean;
}

function formatCurrency(value: number): string {
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="animate-pulse">
          <div className="flex justify-between mb-1">
            <div className="h-3 bg-surface-light rounded w-24" />
            <div className="h-3 bg-surface-light rounded w-12" />
          </div>
          <div className="h-2 bg-surface-light rounded" />
        </div>
      ))}
    </div>
  );
}

export function SectorBreakdown({ sectors, isLoading }: SectorBreakdownProps) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-border bg-surface-light/30 p-4">
        <h3 className="text-sm font-medium text-text-muted uppercase tracking-wider mb-3">Sectors</h3>
        <LoadingSkeleton />
      </div>
    );
  }

  if (sectors.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface-light/30 p-4">
        <h3 className="text-sm font-medium text-text-muted uppercase tracking-wider mb-3">Sectors</h3>
        <p className="text-xs text-text-muted text-center py-4">No sector data available</p>
      </div>
    );
  }

  const maxPct = sectors[0]?.percentage || 100;

  return (
    <div className="rounded-xl border border-border bg-surface-light/30 p-4">
      <h3 className="text-sm font-medium text-text-muted uppercase tracking-wider mb-3">Sectors</h3>

      <div className="space-y-2.5">
        {sectors.map((sector) => (
          <div key={sector.sector}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-text-primary truncate max-w-[60%]">{sector.sector}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-text-muted tabular-nums">
                  {formatCurrency(sector.value)}
                </span>
                <span className="text-xs text-text-secondary tabular-nums font-medium w-10 text-right">
                  {sector.percentage.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="h-1.5 bg-surface rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  'bg-gradient-to-r from-[#D4A574] to-[#D4A574]/60'
                )}
                style={{ width: `${(sector.percentage / maxPct) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-2 border-t border-border">
        <div className="flex items-center justify-between text-[10px] text-text-muted">
          <span>{sectors.length} sector{sectors.length !== 1 ? 's' : ''}</span>
          <span>
            {sectors.reduce((sum, s) => sum + s.count, 0)} positions
          </span>
        </div>
      </div>
    </div>
  );
}
